import { useTranslations } from 'next-intl';

import { type NameType, type ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { type TooltipContentProps } from 'recharts/types/component/Tooltip';

import { Flex } from '@/components/common/Flex';
import { ArrowDownIcon } from '@/components/common/icons/ArrowDownIcon';
import { ArrowUpIcon } from '@/components/common/icons/ArrowUpIcon';
import { Text } from '@/components/common/Text';
import { formatRevenueChartTooltipLabel, type RevenueChartGroupBy } from '@/utils/date';
import { formatAmount, formatPercent, numericToNumber } from '@/utils/number';

import styles from './RevenueChartTooltip.module.scss';

type RevenueChartTooltipProps = TooltipContentProps<ValueType, NameType> & {
  category: 'revenue' | 'average_bill' | 'return' | 'amount';
  currency: string;
  currencyIcon?: string;
  groupBy: RevenueChartGroupBy;
};

const formatChartValue = (
  value: number,
  category: RevenueChartTooltipProps['category'],
  currencyIcon?: string,
  currency?: string
) => {
  if (category === 'amount') {
    return String(value);
  }

  return formatAmount(value, currencyIcon ?? currency);
};

export const RevenueChartTooltip = (props: RevenueChartTooltipProps) => {
  const t = useTranslations('pages.dashboard.widgets.revenue.tooltip');

  const { payload, category, currency, currencyIcon, groupBy } = props;
  const point = payload.at(0);

  if (!point?.payload || typeof point.payload !== 'object') {
    return null;
  }

  const chartPoint = point.payload as Record<string, string | number>;
  const currentValue = numericToNumber(chartPoint['line-current']);
  const previousValue = numericToNumber(chartPoint.comparison_value);
  const hasComparison = chartPoint.comparison_value != null;
  const currentDateLabel = formatRevenueChartTooltipLabel(String(chartPoint.date ?? ''), groupBy);
  const previousDateLabel = chartPoint.comparison_date
    ? formatRevenueChartTooltipLabel(String(chartPoint.comparison_date), groupBy)
    : '';
  const difference = currentValue - previousValue;
  const differencePercent =
    previousValue === 0
      ? currentValue > 0
        ? 1
        : 0
      : previousValue > 0 && currentValue === 0
        ? -1
        : (currentValue - previousValue) / previousValue;
  const isPositive = difference > 0;
  const isNegative = difference < 0;
  const DiffIcon = isPositive ? ArrowUpIcon : isNegative ? ArrowDownIcon : null;
  const diffColor = isPositive ? 'green' : isNegative ? 'red-40p' : 'white-50p';

  return (
    <Flex className={styles.root} direction="column" gap={2}>
      <Text type="medium16" color="white">
        {currentDateLabel}
      </Text>

      <Flex direction="column" gap={1}>
        <Text type="regular14" color="white-50p">
          {t('current')}: {formatChartValue(currentValue, category, currencyIcon, currency)}
        </Text>

        {hasComparison && (
          <>
            <Text type="regular14" color="white-50p">
              {t('previous')}: {formatChartValue(previousValue, category, currencyIcon, currency)}
              {previousDateLabel ? ` (${previousDateLabel})` : ''}
            </Text>

            <Flex gap={1} alignItems="center" wrap="wrap">
              <Text type="regular14" color={diffColor}>
                {t('difference')}: {difference >= 0 ? '+' : '-'}
                {formatChartValue(Math.abs(difference), category, currencyIcon, currency)}
              </Text>

              {DiffIcon && <DiffIcon size={14} color={diffColor} />}

              <Text type="regular14" color={diffColor}>
                ({formatPercent(Math.abs(differencePercent))})
              </Text>
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
};
